"use client";

import { useEntityInstanceHistory, usePyloUsersByIds } from "@/hooks/use-entity-instance-history";
import { useMemo, useState } from "react";
import { format, formatDistanceToNow } from "date-fns";
import {
  ChevronDown,
  ChevronRight,
  KeyRound,
  Plus,
  Pencil,
  Trash2,
  RefreshCcw,
} from "lucide-react";
import {
  EntityInstanceHistoryEvent,
  PyloUserDisplay,
  getPyloUserDisplay,
} from "@/services/entity-instance-history.service";
import { cn } from "@/lib/utils";
import { Button } from "../ui/button";

const SYSTEM_FIELDS_TO_HIDE = new Set([
  "id",
  "integer_id",
  "created_at",
  "updated_at",
  "pylo_customer_id",
]);

type ActivityKind = "create" | "update" | "delete";

interface FieldChange {
  field: string;
  oldValue: unknown;
  newValue: unknown;
  hasOldValue: boolean;
}

interface RelationChange {
  field: string;
  connectedIds: string[];
  disconnectedIds: string[];
}

interface ActivityItem {
  key: string;
  ts: string;
  userId?: string | null;
  kind: ActivityKind;
  changes?: FieldChange[];
  relations?: RelationChange[];
}

const KIND_META: Record<
  ActivityKind,
  {
    icon: React.ComponentType<{ className?: string }>;
    color: string;
  }
> = {
  create: { icon: Plus, color: "text-green-600 bg-green-50" },
  update: { icon: Pencil, color: "text-blue-600 bg-blue-50" },
  delete: { icon: Trash2, color: "text-red-600 bg-red-50" },
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const eventToActivity = (
  event: EntityInstanceHistoryEvent,
  eventIdx: number,
): ActivityItem | null => {
  const userId = event.properties?.pylo_user_id ?? null;
  const baseKey = `${event.ts}-${eventIdx}`;

  if (event.event_name.startsWith("entity.create.")) {
    return { key: `${baseKey}-create`, ts: event.ts, userId, kind: "create" };
  }
  if (event.event_name.startsWith("entity.delete.")) {
    return { key: `${baseKey}-delete`, ts: event.ts, userId, kind: "delete" };
  }
  const updated = (event.properties?.fields_updated ?? {}) as Record<string, unknown>;
  const previous = (event.properties?.fields_previous ?? {}) as Record<string, unknown>;
  const fields = Object.keys(updated).filter((k) => !SYSTEM_FIELDS_TO_HIDE.has(k));
  const changes: FieldChange[] = fields.map((field) => ({
    field,
    newValue: updated[field],
    oldValue: previous[field],
    hasOldValue: Object.prototype.hasOwnProperty.call(previous, field),
  }));

  const relationsRaw = event.properties?.relations_updated ?? {};
  const relations: RelationChange[] = Object.keys(relationsRaw).flatMap((field) => {
    const ops = relationsRaw[field] ?? {};
    const connectedIds = (ops.connected ?? []).map((x) => x.id);
    // Backend emits both `disconnected` and the typo'd `disonnected` from
    // different code paths; merge both.
    const disconnectedIds = [...(ops.disconnected ?? []), ...(ops.disonnected ?? [])].map(
      (x) => x.id,
    );
    if (connectedIds.length === 0 && disconnectedIds.length === 0) return [];
    return [{ field, connectedIds, disconnectedIds }];
  });

  if (changes.length === 0 && relations.length === 0) return null;
  return {
    key: `${baseKey}-update`,
    ts: event.ts,
    userId,
    kind: "update",
    changes,
    relations,
  };
};

interface EntityInstanceActivityFeedProps {
  entityName: string;
  entityInstanceId: string;
  /** Optional id for scroll-to-anchor from the header badge */
  anchorId?: string;
  /** Whether the feed body is shown */
  expanded: boolean;
  /** Toggle handler for the section header */
  onToggle: () => void;
}

export const EntityInstanceActivityFeed: React.FC<EntityInstanceActivityFeedProps> = ({
  entityName,
  entityInstanceId,
  anchorId,
  expanded,
  onToggle,
}) => {
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isFetching,
  } = useEntityInstanceHistory({
    entityName,
    entityInstanceId: expanded ? entityInstanceId : undefined,
  });

  const events = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);

  const activities = useMemo(
    () =>
      events.map((e, idx) => eventToActivity(e, idx)).filter((a): a is ActivityItem => a !== null),
    [events],
  );

  const userIds = useMemo(() => {
    const ids = new Set<string>();
    activities.forEach((a) => {
      if (a.userId) ids.add(a.userId);
    });
    return Array.from(ids);
  }, [activities]);

  const { data: users } = usePyloUsersByIds(userIds);
  const userMap = useMemo(() => {
    const m = new Map<string, PyloUserDisplay>();
    users?.forEach((u) => m.set(u.id, getPyloUserDisplay(u)));
    return m;
  }, [users]);

  return (
    <section
      id={anchorId}
      className="flex min-w-0 flex-col gap-3 overflow-x-hidden border-t border-gray-200 pt-6"
    >
      <header className="flex items-center justify-between">
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-1 text-sm font-semibold text-gray-900"
        >
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
          )}
          Activity
        </button>
        {expanded && (
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            title="Refresh"
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <RefreshCcw className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} />
          </button>
        )}
      </header>

      {expanded && (
        <>
          {isLoading && (
            <ol aria-busy="true" className="relative flex animate-pulse flex-col gap-2.5">
              {[0, 1, 2].map((i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="h-[22px] w-[22px] shrink-0 rounded-full bg-gray-100" />
                  <div className="h-3 rounded bg-gray-100" style={{ width: `${60 - i * 10}%` }} />
                </li>
              ))}
            </ol>
          )}

          {isError && (
            <div className="py-2 text-sm text-red-600">
              Could not load activity: {(error as Error)?.message ?? "Unknown error"}
            </div>
          )}

          {!isLoading && !isError && activities.length === 0 && (
            <div className="py-2 text-sm text-gray-500">No activity yet for this {entityName}.</div>
          )}

          {activities.length > 0 && (
            <ol className="relative flex flex-col gap-2.5">
              {activities.map((a, idx) => {
                const user: PyloUserDisplay = a.userId
                  ? (userMap.get(a.userId) ?? {
                      label: `user ${a.userId.slice(0, 8)}…`,
                      isApiKey: false,
                    })
                  : { label: "system", isApiKey: false };
                const isLast = idx === activities.length - 1;
                return <ActivityRow key={a.key} activity={a} user={user} isLast={isLast} />;
              })}
            </ol>
          )}

          {hasNextPage && (
            <div className="flex justify-center pt-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={isFetchingNextPage}
                onClick={() => fetchNextPage()}
              >
                {isFetchingNextPage ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
};

interface ActivityRowProps {
  activity: ActivityItem;
  user: PyloUserDisplay;
  isLast: boolean;
}

const UserLabel: React.FC<{ user: PyloUserDisplay; className?: string }> = ({
  user,
  className,
}) => (
  <span
    className={cn(
      "inline-flex min-w-0 items-baseline gap-1 break-words font-medium text-gray-900",
      className,
    )}
  >
    {user.isApiKey && (
      <KeyRound className="h-3 w-3 self-center text-gray-500" aria-label="API key" />
    )}
    {user.label}
  </span>
);

const ActivityRow: React.FC<ActivityRowProps> = ({ activity, user, isLast }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = KIND_META[activity.kind];
  const Icon = meta.icon;

  const isUpdate = activity.kind === "update";
  // Anything to expand: update diff, or (on small screens) the user name.
  const hasUpdateDetails = isUpdate;

  let timestamp: Date | null = null;
  try {
    timestamp = new Date(activity.ts);
    if (isNaN(timestamp.getTime())) timestamp = null;
  } catch {
    timestamp = null;
  }

  const toggle = () => setExpanded((v) => !v);

  return (
    <li className="relative">
      {!isLast && <div className="absolute left-[11px] top-6 h-full w-px bg-gray-200" />}
      <button
        type="button"
        onClick={toggle}
        className="flex w-full min-w-0 items-center gap-3 text-left"
      >
        <div
          className={cn(
            "z-10 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full",
            meta.color,
          )}
        >
          <Icon className="h-3 w-3" />
        </div>
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-1 text-sm">
          <UserLabel user={user} className="hidden sm:inline-flex" />
          <ActivityHeaderText activity={activity} />
          {timestamp && (
            <span
              className="text-xs text-gray-400"
              title={format(timestamp, "yyyy-MM-dd HH:mm:ss")}
            >
              · {formatDistanceToNow(timestamp, { addSuffix: true })}
            </span>
          )}
        </div>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform",
            expanded && "rotate-180",
            // On large screens, only show the chevron when there's diff details to reveal.
            !hasUpdateDetails && "sm:hidden",
          )}
        />
      </button>
      {expanded && (
        <div
          className={cn(
            "ml-9 mt-1 flex min-w-0 flex-col gap-1 text-sm text-gray-500",
            // On large screens for create/delete, the expansion would only contain the user
            // name (already visible in the header), so hide it.
            !hasUpdateDetails && "sm:hidden",
          )}
        >
          <UserLabel user={user} className="sm:hidden" />
          {hasUpdateDetails && (
            <>
              {(activity.changes ?? []).map((c) => (
                <FieldDiff key={`f-${c.field}`} change={c} />
              ))}
              {(activity.relations ?? []).map((r) => (
                <RelationDiff key={`r-${r.field}`} change={r} />
              ))}
            </>
          )}
        </div>
      )}
    </li>
  );
};

const MAX_FIELDS_INLINE = 3;

const ActivityHeaderText: React.FC<{ activity: ActivityItem }> = ({ activity }) => {
  if (activity.kind === "create") {
    return <span className="text-gray-600">created</span>;
  }
  if (activity.kind === "delete") {
    return <span className="text-gray-600">deleted</span>;
  }
  const fieldNames = [
    ...(activity.changes ?? []).map((c) => c.field),
    ...(activity.relations ?? []).map((r) => r.field),
  ];
  if (fieldNames.length > MAX_FIELDS_INLINE) {
    return (
      <>
        <span className="text-gray-600">updated</span>
        <span className="text-gray-800">{fieldNames.length} fields</span>
      </>
    );
  }
  return (
    <>
      <span className="text-gray-600">updated</span>
      {fieldNames.map((name, i) => (
        <span key={name} className="flex min-w-0 items-baseline gap-x-1">
          <code className="break-all rounded bg-gray-100 px-1 py-px text-xs font-medium text-gray-800">
            {name}
          </code>
          {i < fieldNames.length - 1 && <span className="text-gray-400">,</span>}
        </span>
      ))}
    </>
  );
};

const RelationDiff: React.FC<{ change: RelationChange }> = ({ change }) => {
  const parts: React.ReactNode[] = [];
  if (change.connectedIds.length > 0) {
    parts.push(
      <span key="connected" className="text-gray-500">
        {change.connectedIds.length} connected
      </span>,
    );
  }
  if (change.disconnectedIds.length > 0) {
    parts.push(
      <span key="disconnected" className="text-gray-500">
        {change.disconnectedIds.length} disconnected
      </span>,
    );
  }
  return (
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-1 text-xs">
      <code className="break-all rounded bg-gray-100 px-1 py-px font-medium text-gray-800">
        {change.field}
      </code>
      {parts.map((part, i) => (
        <span key={i} className="flex items-baseline gap-x-1">
          {part}
          {i < parts.length - 1 && <span className="text-gray-400">,</span>}
        </span>
      ))}
    </div>
  );
};

const FieldDiff: React.FC<{ change: FieldChange }> = ({ change }) => {
  return (
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-1">
      <code className="break-all rounded bg-gray-100 px-1 py-px text-xs font-medium text-gray-800">
        {change.field}
      </code>
      {change.hasOldValue ? (
        <>
          <span className="text-gray-500">from</span>
          <code className="break-all rounded bg-gray-50 px-1 py-px text-xs text-gray-500 line-through">
            {formatValue(change.oldValue)}
          </code>
          <span className="text-gray-500">to</span>
          <code className="break-all rounded bg-gray-100 px-1 py-px text-xs font-medium text-gray-900">
            {formatValue(change.newValue)}
          </code>
        </>
      ) : (
        <>
          <span className="text-gray-500">to</span>
          <code className="break-all rounded bg-gray-100 px-1 py-px text-xs font-medium text-gray-900">
            {formatValue(change.newValue)}
          </code>
        </>
      )}
    </div>
  );
};
